import useVehicleHistory, { mergeVehicleFields, vehicleCustomer, vehicleDetails } from "./useVehicleHistory";
// src/components/comenzi/Create.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Alert,
  Spinner,
  Badge,
  Form,
  Card,
  InputGroup,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";
import ClientPickerModal from "../pickers/ClientPickerModal";
import PartPickerModal from "../pickers/PartPickerModal";
import ClientRepairFields from './ClientRepairFields';
import ServiceInitialFields from './ServiceInitialFields';
import ShopSupplyPanel from './ShopSupplyPanel';

export default function ClientOrderCreate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [isService, setIsService] = useState(false);
  const [isShop, setIsShop] = useState(false);
  const [serviceDetails, setServiceDetails] = useState({});

  // Nomenclators
  const [currencies, setCurrencies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [gestiuni, setGestiuni] = useState([]);

  // Pickers active states
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [partPickerOpen, setPartPickerOpen] = useState(false);
  const [activeLineIdx, setActiveLineIdx] = useState(null);

  // Form State
  const [header, setHeader] = useState({
    bxnumar_comanda: "",
    bxdescr: "",
    bxcustomer_id: "",
    customer_name: "",
    bxdata_comanda: new Date().toISOString().slice(0, 10),
    bxunitate_id: "",
    tipcomanda_id: "2", // Default to Domestic (RO)
    bxvaluta_id: "",
    bxsasiu: "",
    comision: "",
  });

  const vinMessage = useVehicleHistory(header.bxsasiu, isService && !isEdit, (data, previous) => {
    setHeader(current => mergeVehicleFields(current, vehicleCustomer(data), vehicleCustomer(previous)));
    setServiceDetails(current => mergeVehicleFields(current, vehicleDetails(data, true), vehicleDetails(previous, true)));
  });

  const [lines, setLines] = useState([
    {
      bxpart_id: "",
      part_code: "",
      part_name: "",
      bxqtyrequest: 1,
      bxpret_vinzare: 0,
      bxgestiune_id: "",
      termen_livrare2: "", // WO
      comentariu: "",
    },
  ]);

  // Load Nomenclators and Auto-Number
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const firmResponse = await authFetch('/firma');
        setIsService(firmResponse?.items?.[0]?.app_variant === 'SERVICE_AUTO');
        // Load currencies
        const valData = await authFetch("/bxvaluta?limit=100");
        const valList = valData?.items || [];
        setCurrencies(valList);

        // Load branches/unitate
        const branchData = await authFetch("/bxunitate");
        setBranches(branchData || []);

        // Load gestiuni
        const gestData = await authFetch("/bxgestiune?limit=100");
        setGestiuni(gestData?.items || []);

        if (isEdit) {
          const order = await authFetch(`/comenzi/client/${id}`);
          if (order) {
            setIsShop(Boolean(order.din_site));
            setServiceDetails(order.service_details || {});
            setHeader({
              bxnumar_comanda: order.bxnumar_comanda || "",
              bxdescr: order.bxdescr || "",
              bxcustomer_id: String(order.bxcustomer_id || ""),
              customer_name: order.customer_name || "",
              bxdata_comanda: order.bxdata_comanda
                ? order.bxdata_comanda.slice(0, 10)
                : "",
              bxunitate_id: String(order.bxunitate_id || ""),
              tipcomanda_id: String(order.tipcomanda_id ?? 2),
              bxvaluta_id: String(order.bxvaluta_id || ""),
              bxsasiu: order.bxsasiu || "",
              comision: order.comision || "",
            });

            setLines(
              order.linii.map((ln) => ({
                bxid: ln.bxid,
                bxpart_id: String(ln.bxpart_id || ""),
                part_code: ln.part_code || "",
                part_name: ln.part_name || "",
                bxqtyrequest: ln.bxqtyrequest || 1,
                bxpret_vinzare: ln.bxpret_vinzare || 0,
                bxgestiune_id: ln.bxgestiune_id ? String(ln.bxgestiune_id) : "",
                termen_livrare2: ln.termen_livrare2 || "",
                comentariu: ln.comentariu || "",
              })),
            );
          }
        } else {
          // Prepopulate defaults if lists are loaded
          if (valList.length > 0) {
            const leiVal =
              valList.find((v) => v.cod?.toUpperCase() === "LEI") || valList[0];
            setHeader((prev) => ({ ...prev, bxvaluta_id: String(leiVal.id) }));
          }

          if (branchData.length > 0) {
            setHeader((prev) => ({
              ...prev,
              bxunitate_id: String(branchData[0].id),
            }));
          }

          // Fetch next sequential number
          const nextIdRes = await authFetch("/next-id/COMTOP");
          if (nextIdRes && nextIdRes.id) {
            setHeader((prev) => ({
              ...prev,
              bxnumar_comanda: String(nextIdRes.id),
            }));
          }
        }
      } catch (err) {
        console.error("Eroare la încărcarea nomenclatoarelor:", err);
        setError(
          "Nu s-au putut încărca datele de configurare. Reîncărcați pagina.",
        );
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [isEdit, id]);

  // Recalculate line total value
  const getLineValue = (line) => {
    return (
      (Number(line.bxqtyrequest) || 0) * (Number(line.bxpret_vinzare) || 0)
    );
  };

  // Recalculate order total value
  const getOrderTotal = () => {
    return lines.reduce((acc, curr) => acc + getLineValue(curr), 0);
  };

  // Handle line field change
  const handleLineChange = (idx, field, value) => {
    const nextLines = [...lines];
    nextLines[idx] = {
      ...nextLines[idx],
      [field]: value,
    };
    setLines(nextLines);
  };

  // Add new empty line
  const handleAddLine = () => {
    setLines([
      ...lines,
      {
        bxpart_id: "",
        part_code: "",
        part_name: "",
        bxqtyrequest: 1,
        bxpret_vinzare: 0,
        bxgestiune_id: gestiuni[0]?.id ? String(gestiuni[0].id) : "",
        termen_livrare2: "",
        comentariu: "",
      },
    ]);
  };

  // Remove line
  const handleRemoveLine = (idx) => {
    if (lines.length === 1) {
      toast.warning("Comanda trebuie să aibă cel puțin o linie.");
      return;
    }
    setLines(lines.filter((_, i) => i !== idx));
  };

  // Pick Client Handler
  const handlePickClient = (client) => {
    setHeader((prev) => ({
      ...prev,
      bxcustomer_id: client.id,
      customer_name: client.raw?.bxdenumire || client.raw?.name || client.label,
    }));
    setClientPickerOpen(false);
  };

  // Pick Part Handler
  const handlePickPart = (part) => {
    const nextLines = [...lines];
    if (activeLineIdx !== null && nextLines[activeLineIdx]) {
      nextLines[activeLineIdx] = {
        ...nextLines[activeLineIdx],
        bxpart_id: part.id,
        part_code: part.raw?.cod || part.label,
        part_name: part.raw?.denumire || part.label,
      };
      setLines(nextLines);
    }
    setPartPickerOpen(false);
    setActiveLineIdx(null);
  };

  // Submit Save
  const handleSave = async (e) => {
    e.preventDefault();
    const printAfterSave = e.nativeEvent?.submitter?.value === 'print';
    const generateEstimate = e.nativeEvent?.submitter?.value === 'deviz';
    setError("");

    // Validation
    if (!header.bxnumar_comanda.trim()) {
      setError("Numărul de comandă este obligatoriu.");
      return;
    }
    if (!isService && !header.bxdescr.trim()) {
      setError("Descrierea (Livrare) este obligatorie.");
      return;
    }
    if (!header.bxcustomer_id) {
      setError("Vă rugăm să alegeți un client.");
      return;
    }

    // A reception order may precede the detailed estimate.
    const linesToSave = isService ? lines.filter(ln => ln.bxpart_id || ln.comentariu?.trim() || ln.bxid) : lines;
    for (let i = 0; i < linesToSave.length; i++) {
      const ln = linesToSave[i];
      if (!ln.bxpart_id) {
        setError(`Linia ${i + 1} nu are selectat niciun produs/piesă.`);
        return;
      }
      if ((Number(ln.bxqtyrequest) || 0) <= 0) {
        setError(
          `Linia ${i + 1} trebuie să aibă o cantitate cerută mai mare decât 0.`,
        );
        return;
      }
    }

    setSaving(true);
    try {
      const method = isEdit ? "PUT" : "POST";
      const url = isEdit ? `/comenzi/client/${id}` : "/comenzi/client";

      const linesPayload = linesToSave.map((ln) => {
        const lineItem = {
          bxpart_id: Number(ln.bxpart_id),
          bxqtyrequest: Number(ln.bxqtyrequest),
          bxum_zecimale: 0,
          bxpret_vinzare: Number(ln.bxpret_vinzare) || 0.0,
          bxlamount: getLineValue(ln),
          bxqtyrez: 0.0,
          bxfacturat: 0.0,
          bxcomandat: 0.0,
          bxstatus: 0, // PRE
          bxcustomer_id: Number(header.bxcustomer_id),
          ramas: Math.ceil(Number(ln.bxqtyrequest)),
          predat: 0.0,
          termen_livrare1: `${header.bxdata_comanda}T00:00:00`,
          termen_livrare2: ln.termen_livrare2.trim() || null,
          comentariu: ln.comentariu.trim() || null,
          bxgestiune_id: ln.bxgestiune_id ? Number(ln.bxgestiune_id) : null,
          bxvaluta_id: Number(header.bxvaluta_id),
        };
        if (isEdit && ln.bxid) {
          lineItem.bxid = ln.bxid;
        }
        return lineItem;
      });

      const payload = {
        din_site: isShop ? 1 : 0,
        bxnumar_comanda: header.bxnumar_comanda.trim(),
        bxdescr: header.bxdescr.trim() || (isService ? 'Reparație auto' : ''),
        service_details: serviceDetails,
        bxcustomer_id: Number(header.bxcustomer_id),
        bxdata_comanda: `${header.bxdata_comanda}T00:00:00`,
        bxunitate_id: Number(header.bxunitate_id),
        tipcomanda_id: Number(header.tipcomanda_id),
        bxvaluta_id: Number(header.bxvaluta_id),
        bxsasiu: header.bxsasiu.trim() || null,
        comision: header.comision.trim() || null,
        linii: linesPayload,
      };

      const saved = await authFetch(url, {
        method: method,
        body: JSON.stringify(payload),
      });

      toast.success("Comanda client a fost salvată cu succes!");
      if (generateEstimate) {
        const deviz = await authFetch(`/comenzi/client/${saved.bxid || id}/genereaza-deviz`, { method: 'POST' });
        navigate(`/comenzi/intern/linii?open=${deviz.bxid}`);
      } else if (printAfterSave) {
        navigate(`/comenzi/client/${saved.bxid || id}/print`);
      } else if (isService) {
        navigate(`/comenzi/client/edit/${saved.bxid || id}`);
      } else {
        navigate("/comenzi/client/linii");
      }
    } catch (err) {
      setError(err.message || "A apărut o eroare la salvarea comenzii.");
      toast.error("Salvare eșuată!");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="text-muted mt-2">Se încarcă configurările...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="px-4 py-4">
      {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
          <div>
            <h2 className="fw-bold text-dark mb-1">
              {isEdit
                ? `Editare Comandă Client #${header.bxnumar_comanda}`
                : "Creează Comandă Client"}
            </h2>
            <p className="text-muted mb-0">
              {isService ? 'Recepție autovehicul, constatare și acorduri client.' : isEdit
                ? "Modificare comandă master-detail în sistem."
                : "Adăugare comandă master-detail în sistem."}
            </p>
          </div>
          <div className="d-flex gap-2">
            {isEdit && !isShop && (
              <Button
                variant="outline-success"
                type={isService ? 'submit' : 'button'}
                form={isService ? 'client-order-form' : undefined}
                value="deviz"
                disabled={saving}
                className="fw-semibold shadow-sm"
                onClick={async () => {
                  if (isService) return;
                  if(!window.confirm("Generați automat comenzile de producție pentru aceste repere?")) return;
                  try {
                    const res = await authFetch(`/comenzi-prod/generate-from-client-order/${id}`, { method: 'POST' });
                    if (res && res.created_ids) {
                      toast.success(res.message || "Comenzile au fost generate cu succes!");
                    } else {
                      toast.success("Comenzile au fost generate!");
                    }
                  } catch (e) {
                    toast.error(e.message || "Eroare la generarea comenzilor.");
                  }
                }}
              >
                {isService ? 'Generează / deschide deviz' : '🚀 Generează Comenzi Producție'}
              </Button>
            )}
            <Button
              variant="outline-secondary"
              onClick={() => navigate(-1)}
              className="fw-semibold"
            >
              &larr; Înapoi la listă
            </Button>
          </div>
        </div>

      {error && (
        <Alert variant="danger" className="shadow-sm">
          {error}
        </Alert>
      )}

      {isShop && isEdit && <ShopSupplyPanel orderId={Number(id)}/>}

      <Form id="client-order-form" onSubmit={handleSave}>
        {isService && !isShop && <Card className="mb-3"><Card.Body><Row><Col md={5}>
                <Form.Group controlId="service-vin">
                  <Form.Label className="fw-semibold text-muted small text-uppercase">
                    Serie Șasiu / Identificare
                  </Form.Label>
                  <Form.Control
                    type="text"
                    autoFocus={isService}
                    autoComplete="off"
                    placeholder="Serie șasiu vehicul (dacă se aplică)"
                    value={header.bxsasiu}
                    onChange={(e) =>
                      setHeader({ ...header, bxsasiu: e.target.value })
                    }
                  />
                      {vinMessage && <Form.Text role="status">{vinMessage}</Form.Text>}
                </Form.Group>
              </Col></Row></Card.Body></Card>}
        {!isShop && <Card className="shadow-sm mb-3"><Card.Body>
          <ServiceInitialFields value={serviceDetails} onChange={setServiceDetails} />
        </Card.Body></Card>}
        {isService && !isShop && <div className="d-flex justify-content-end mb-3">
          <Button type="submit" value="print" disabled={saving}>Print comandă</Button>
        </div>}
        {/* Header Details Card */}
        <Card className="shadow-sm border-0 mb-4 bg-light">
          <Card.Body className="p-4">
            <h5 className="fw-bold text-dark border-bottom pb-2 mb-3">
              {isService ? 'Date comandă și client' : 'Detalii Antet Comandă'}
            </h5>

            <Row className="g-3">
              {/* Order Number */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small text-uppercase">
                    Număr Comandă <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={header.bxnumar_comanda}
                    onChange={(e) =>
                      setHeader({ ...header, bxnumar_comanda: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </Col>

              {/* Order Date */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small text-uppercase">
                    Dată Comandă <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={header.bxdata_comanda}
                    onChange={(e) =>
                      setHeader({ ...header, bxdata_comanda: e.target.value })
                    }
                    required
                  />
                </Form.Group>
              </Col>

              {/* Client Picker */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small text-uppercase">
                    Client <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Alegeți un client din listă..."
                      value={header.customer_name}
                      readOnly
                      required
                    />
                    <Button
                      variant="outline-primary"
                      onClick={() => setClientPickerOpen(true)}
                    >
                      Alege Client
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>

              {/* Description / Livrare */}
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small text-uppercase">
                    Descriere / Livrare <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={isService ? 'Reparație auto' : 'Detalii despre destinație / modalitate livrare'}
                    value={header.bxdescr}
                    onChange={(e) =>
                      setHeader({ ...header, bxdescr: e.target.value })
                    }
                    required={!isService}
                  />
                </Form.Group>
              </Col>

              {/* Unitate (Branch) */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small text-uppercase">
                    Branch / Punct Lucru
                  </Form.Label>
                  <Form.Select
                    value={header.bxunitate_id}
                    onChange={(e) =>
                      setHeader({ ...header, bxunitate_id: e.target.value })
                    }
                  >
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.descriere || b.cod || b.id}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Tip Comanda */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small text-uppercase">
                    Tip Comandă
                  </Form.Label>
                  <Form.Select
                    value={header.tipcomanda_id}
                    onChange={(e) =>
                      setHeader({ ...header, tipcomanda_id: e.target.value })
                    }
                  >
                    <option value="2">Clienti </option>
                    <option value="1">Intern </option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Valuta */}
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small text-uppercase">
                    Valută
                  </Form.Label>
                  <Form.Select
                    value={header.bxvaluta_id}
                    onChange={(e) =>
                      setHeader({ ...header, bxvaluta_id: e.target.value })
                    }
                  >
                    {currencies.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.cod}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Serie Sasiu */}
              {!isService && !isShop && (<Col md={5}>
                <Form.Group controlId="service-vin">
                  <Form.Label className="fw-semibold text-muted small text-uppercase">
                    Serie Șasiu / Identificare
                  </Form.Label>
                  <Form.Control
                    type="text"
                    autoFocus={isService}
                    autoComplete="off"
                    placeholder="Serie șasiu vehicul (dacă se aplică)"
                    value={header.bxsasiu}
                    onChange={(e) =>
                      setHeader({ ...header, bxsasiu: e.target.value })
                    }
                  />
                      {vinMessage && <Form.Text role="status">{vinMessage}</Form.Text>}
                </Form.Group>
              </Col>)}

              {/* Comision */}
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold text-muted small text-uppercase">
                    Comision
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Detalii / Procent comision"
                    value={header.comision}
                    onChange={(e) =>
                      setHeader({ ...header, comision: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {isService && !isShop && <Card className="shadow-sm mb-4"><Card.Body>
          <h4 className="border-bottom pb-2">Comandă de reparație — recepție și acorduri</h4>
          <ClientRepairFields value={serviceDetails} onChange={setServiceDetails} />
        </Card.Body></Card>}
        {/* Lines Details Card */}
        <Card className="shadow-sm border-0 mb-4">
          <Card.Body className="p-0">
            <div className="d-flex justify-content-between align-items-center p-3 bg-light border-bottom">
              <h5 className="fw-bold text-dark mb-0">{isService ? 'Lucrări / articole pentru deviz' : 'Linii Comandă Client'}</h5>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddLine}
                className="fw-semibold"
              >
                + Adaugă Linie
              </Button>
            </div>

            <div className="table-responsive">
              <Table
                hover
                striped
                className="align-middle mb-0 text-nowrap"
                size="sm"
              >
                <thead className="table-secondary">
                  <tr>
                    <th className="text-center" style={{ width: "40px" }}>
                      No
                    </th>
                    <th style={{ width: "320px" }}>Produs / Piesă</th>
                    <th className="text-center" style={{ width: "100px" }}>
                      Cantitate Cerută
                    </th>
                    <th className="text-end" style={{ width: "120px" }}>
                      Preț Vânzare
                    </th>
                    <th className="text-end" style={{ width: "120px" }}>
                      Valoare
                    </th>
                    {/* <th style={{ width: "150px" }}>Gestiune</th> */}
                    <th style={{ width: "120px" }}>WO (Termen Liv2)</th>
                    <th>Comentariu</th>
                    <th className="text-center" style={{ width: "60px" }}>
                      Șterge
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((ln, idx) => (
                    <tr key={idx}>
                      <td className="text-center fw-semibold text-muted">
                        {idx + 1}
                      </td>
                      <td>
                        <InputGroup size="sm">
                          <Form.Control
                            type="text"
                            placeholder="Cod - Denumire..."
                            value={
                              ln.part_code
                                ? `${ln.part_code} - ${ln.part_name}`
                                : ""
                            }
                            readOnly
                            required
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              setActiveLineIdx(idx);
                              setPartPickerOpen(true);
                            }}
                          >
                            Caută
                          </Button>
                        </InputGroup>
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-center"
                          value={ln.bxqtyrequest}
                          min="0.001"
                          step="any"
                          onChange={(e) =>
                            handleLineChange(
                              idx,
                              "bxqtyrequest",
                              e.target.value,
                            )
                          }
                          required
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          size="sm"
                          className="text-end"
                          value={ln.bxpret_vinzare}
                          min="0"
                          step="any"
                          onChange={(e) =>
                            handleLineChange(
                              idx,
                              "bxpret_vinzare",
                              e.target.value,
                            )
                          }
                          required
                        />
                      </td>
                      <td className="text-end fw-mono fw-bold">
                        {getLineValue(ln).toFixed(2)}
                      </td>
                      {/* <td>
                        <Form.Select
                          size="sm"
                          value={ln.bxgestiune_id}
                          onChange={(e) =>
                            handleLineChange(
                              idx,
                              "bxgestiune_id",
                              e.target.value,
                            )
                          }
                        >
                          <option value="">-- Alege Gestiune --</option>
                          {gestiuni.map((g) => (
                            <option key={g.id} value={g.id}>
                              {g.denumire || g.cod || g.id}
                            </option>
                          ))}
                        </Form.Select>
                      </td> */}
                      <td>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={ln.termen_livrare2}
                          onChange={(e) =>
                            handleLineChange(
                              idx,
                              "termen_livrare2",
                              e.target.value,
                            )
                          }
                          placeholder="Cod WO"
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          size="sm"
                          value={ln.comentariu}
                          onChange={(e) =>
                            handleLineChange(idx, "comentariu", e.target.value)
                          }
                          placeholder="Observații linie"
                        />
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="py-0 px-2"
                          onClick={() => handleRemoveLine(idx)}
                        >
                          &times;
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
          <Card.Footer className="bg-light d-flex justify-content-between align-items-center py-3">
            <span className="fw-semibold text-muted">
              Total Linii: {lines.length}
            </span>
            <div>
              <span className="fw-bold me-2 text-uppercase text-muted small">
                Total Comandă:
              </span>
              <span className="fs-4 fw-bold text-primary">
                {getOrderTotal().toFixed(2)}
              </span>
            </div>
          </Card.Footer>
        </Card>

        {/* Action Buttons */}
        <div className="d-flex justify-content-end gap-3 mb-5">
          {isService && !isShop && <Button type="submit" value="print" disabled={saving}>Print comandă</Button>}
          <Button
            variant="outline-secondary"
            onClick={() => navigate(-1)}
            disabled={saving}
            className="px-4 fw-semibold"
          >
            Renunță
          </Button>
          <Button
            type="submit"
            variant="success"
            disabled={saving}
            className="px-5 fw-bold"
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Se salvează...
              </>
            ) : (
              "Salvează Comanda"
            )}
          </Button>
        </div>
      </Form>

      {/* Client Picker Modal */}
      <ClientPickerModal
        show={clientPickerOpen}
        onHide={() => setClientPickerOpen(false)}
        onPick={handlePickClient}
      />

      {/* Part/Product Picker Modal */}
      <PartPickerModal
        show={partPickerOpen}
        onHide={() => setPartPickerOpen(false)}
        onPick={handlePickPart}
      />
    </Container>
  );
}
